import { Col, Collapse, Form, Input, Row } from "antd";
import modal from "antd/es/modal";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DraggableModal } from "../../../../components/DraggableModal";
import { MapAddSingleMarker } from "../../../../components/maps/MapAddPoint";
import { SelectOwnerShip } from "../../../../components/selects";
import { GeoJsonPoint, Geometry } from "../../../../models/geoJsonPoint";
import { EntMts } from "../../../../models/mts";
import { TransportItem } from "../../../../models/transportItem";
import { MtsItems } from "./MtsItems";
import SelectTerritorialUnitsCode from "../../../../components/selects/SelectTerritorialUnitsCode";

enum FormField {
  id, // Уникальный идентификатор записи (может быть отсутствовать при создании)
  nameRu, // Наименование на русском (обязательно)
  nameKy, // Наименование на кыргызском (обязательно)
  managerFullName, // ФИО руководителя (обязательно)
  email, // Электронная почта (опционально)
  phone, // Номер телефона (обязательно)
  regionId,
  areaId,
  territorialUnit,
  ownershipFormId, // ID формы собственности (обязательно)

}

interface IProps {
  show: boolean;
  mts: EntMts;
  onHide: () => void;
  onSubmit: (pos: EntMts) => Promise<boolean>;
}

export const AddMtsModal = (props: IProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [geoJsonData, setGeoJsonData] = useState<GeoJsonPoint|null>(null);
  const [transports, setTransports] = useState<TransportItem[]|null>(null);
  const [selectedRegionId, setSelectedRegionId] = useState<number|null>(null);
  const [selectedRegionPolygonJson, setSelectedRegionPolygonJson] = useState<string|null>(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState<string|null>(null);

  const [selectedAreaCode, setSelectedAreaCode] = useState<string|null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number|null>(null);

  useEffect(() => {
	if (props.show) setFieldsFromEntity();
	setSelectedRegionId(props.mts.territorialUnitRegion?.id || null);
	setSelectedAreaId(props.mts.territorialUnitArea?.id || null);
	setSelectedAreaCode(props.mts.territorialUnitArea?.code || null);
  }, [props.show]);

  const setFieldsFromEntity = (): void => {
	setTransports(props.mts.transports)
	form.setFields([
	  { name: FormField.nameKy, value: props.mts.nameKy },
	  { name: FormField.nameRu, value: props.mts.nameRu },
	  { name: FormField.ownershipFormId, value: props.mts.ownershipForm?.id },
	  { name: FormField.regionId, value: props.mts.territorialUnitRegion?.id },
	  { name: FormField.areaId, value: props.mts.territorialUnitArea?.id },
	  { name: FormField.territorialUnit, value: props.mts.territorialUnit?.id },
	  { name: FormField.managerFullName, value: props.mts.managerFullName },
	  { name: FormField.phone, value: props.mts.phone },
	  { name: FormField.email, value: props.mts.email },
	]);
  };
  const updateGeoJsonPoint = () => {
	const latitude = props.mts.latitude;
	const longitude = props.mts.longitude;

	if (latitude !== undefined && longitude !== undefined) {
	  const geoJsonPoint = new GeoJsonPoint();
	  geoJsonPoint.geometry = new Geometry();
	  geoJsonPoint.geometry.coordinates = [longitude, latitude];
	  setGeoJsonData(geoJsonPoint);
	} else {
	  console.error(t('breeding_farms.error_geometry'));
	}
  };
  useEffect(() => {
	if (props.mts.latitude && props.mts.longitude) {
	  updateGeoJsonPoint();
	} else {
	  setGeoJsonData(null);
	}
  }, [props.mts.latitude, props.mts.longitude]);

  const setEntityFromFields = (): EntMts => {
	let res = _.cloneDeep(props.mts);
	res.nameKy = form.getFieldValue(FormField.nameKy);
	res.nameRu = form.getFieldValue(FormField.nameRu);
	res.regionId = form.getFieldValue(FormField.regionId);
	res.areaId = form.getFieldValue(FormField.areaId);
	res.territorialUnitId = form.getFieldValue(FormField.territorialUnit)
	res.ownershipFormId = form.getFieldValue(FormField.ownershipFormId);
	res.managerFullName = form.getFieldValue(FormField.managerFullName);
	res.phone = form.getFieldValue(FormField.phone);
	res.email = form.getFieldValue(FormField.email);
	if (transports) {
	  res.transports = transports;
	}
	if (geoJsonData !== null) {
	  res.latitude = geoJsonData.geometry?.coordinates[1] as number;
	  res.longitude = geoJsonData.geometry?.coordinates[0] as number;
	}
	return res;
  }
  const hideModal = () => {
	props.onHide();
	setTransports(null)
	setSelectedRegionId(null);
	setSelectedRegionPolygonJson(null);
	setSelectedAreaCode(null);
	setGeoJsonData(null);
  }
  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();

	  if (!geoJsonData) {
		modal.error({
		  title: t('msg.error'),
		  content: t('Выберите местоположение перед отправкой формы.')
		});
		return;
	  }

	  const data = setEntityFromFields();
	  const { transports } = data;

	  if (!transports || transports.length === 0) {
		modal.error({
		  title: t('msg.error'),
		  content: t('Добавьте хотя бы одно транспортное средство.')
		});
		return;
	  }

	  const invalidRegisters = transports.filter(
		(transport: TransportItem) => transport.transportTypeId == null || transport.count == null
	  );

	  if (invalidRegisters.length > 0) {
		modal.error({
		  title: t('msg.error'),
		  content: t('Заполните все поля для каждого транспортного средства.')
		});
		return;
	  }

	  const result = await props.onSubmit(data);

	  if (result) hideModal();
	} catch (error: any) {
	  modal.error({
		title: t('msg.error'),
		content: error.message || t('msg.unexpected_error')
	  });
	} finally {
	  setLoading(false);
	}
  };

  return (
	<>
	  <DraggableModal
		title={ props.mts.id === null ? t("mts.add_mts_organizations") : t('mts.edit_mts_organizations') }
		visible={ props.show }
		onCancel={ hideModal }
		onSubmit={ handleSubmit }
		submitLoading={ loading }
		width={ 800 }
	  >
		<Form
		  initialValues={ { remember: true } }
		  autoComplete="off"
		  layout="vertical"
		  form={ form }
		>
		  <Row style={ { margin: 0 } } gutter={ 16 }>
			<Col className="gutter-row" span={ 12 }>
			  <Form.Item
				label={ t('Название на Кырг') }
				name={ FormField.nameKy }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }

			  >
				<Input type="text"/>
			  </Form.Item>
			  <Form.Item
				label={ t('Название на Рус') }
				name={ FormField.nameRu }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }

			  >
				<Input type="text"/>
			  </Form.Item>

			  <Form.Item
				label={ t('titles.territorial_unit_area') }
				name={ FormField.regionId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnitsCode
				  parentId={ 1 }
				  onChange={ (value, option: any) => {
					setSelectedRegionId(Number(value));
					setSelectedRegionPolygonJson(option?.data?.polygonJson);
					setSelectedRegionCode(option?.data?.code);
					setSelectedAreaCode(null);
					setSelectedAreaId(null);
					form.setFieldsValue({
					  [FormField.regionId]: value,
					  [FormField.areaId]: null, // Сбрасываем район
					  [FormField.territorialUnit]: null, // Сбрасываем населенный пункт
					});
				  } }
				/>
			  </Form.Item>
				<>
				  { selectedRegionId || props.mts.territorialUnitRegion ? (
					<Form.Item
					  label={ t('titles.territorialUnitDistricts') }
					  name={ FormField.areaId }
					  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
					>
					  <SelectTerritorialUnitsCode
						type={ ['AREA'] }
						parentId={ selectedRegionId }
						onChange={ (value, option: any) => {
						  setSelectedRegionPolygonJson(option?.data?.polygonJson);
						  setSelectedAreaCode(option?.data?.code || null);
						  setSelectedAreaId(value as number);
						  form.setFields([{ name: FormField.territorialUnit, value: null }]);
						} }
					  />
					</Form.Item>
				  ) : null }

			  { selectedAreaCode || props.mts.territorialUnit ? (
				<Form.Item
				  label={ t("titles.territorialUnitVillage") }
				  name={ FormField.territorialUnit }
				  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
				>
				  <SelectTerritorialUnitsCode areaCode={ selectedAreaCode }/>
				</Form.Item>
			  ) : null }
				</>
			</Col>
			<Col className="gutter-row" span={ 12 }>
			  <Form.Item
				label={ t('mts.forms_of_ownership') }
				name={ FormField.ownershipFormId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }

			  >
				<SelectOwnerShip/>
			  </Form.Item>
			  <Form.Item
				label={ t('mts.supervisor_name') }
				name={ FormField.managerFullName }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }

			  >
				<Input type="text"/>
			  </Form.Item>
			  <Form.Item
				label={ t('user.phone_number') }
				name={ FormField.phone }
				rules={ [
				  {
					pattern: /^996\d{9}$/,
					message: t('user.phone_number_desc')
				  },
				] }
			  >
				<Input count={ {
				  show: true,
				  max: 12,
				} } maxLength={ 12 } placeholder="996777000000" type="number" autoComplete="off"/>
			  </Form.Item>
			  <Form.Item
				label={ t('mts.contact_email_address') }
				name={ FormField.email }

			  >
				<Input type="email"/>
			  </Form.Item>

			</Col>
			<Col className="gutter-row" span={ 24 }>

			  <Collapse
				size="small"
				items={ [{
				  key: '1', label: 'Указать местонахождение МТС', children: <p>
					<MapAddSingleMarker
					  polygonJson={ selectedRegionPolygonJson }
					  setGeoJsonData={ setGeoJsonData }
					  geoJsonData={ geoJsonData }
					/></p>
				}] }
			  />

			</Col>

			<Col className="gutter-row" span={ 24 }>

			  <MtsItems transports={ transports } setTransports={ setTransports }/>
			</Col>
		  </Row>
		</Form>
	  </DraggableModal>
	</>
  )
}
